import PageHeader from "../components/layout/PageHeader";
import StatsSection from "../components/sections/StatsSection";
import CapabilityPillars from "../components/sections/CapabilityPillars";
import ContactCTA from "../components/sections/ContactCTA";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const values = [
    { title: "Precision First", desc: "Every system, every drawing, every calculation — treated as if lives depend on it. Because often, they do." },
    { title: "Integrated Design", desc: "Mechanical, electrical and plumbing designed as one system from day one — never bolted together later." },
    { title: "Owner-Aligned", desc: "We optimize for total cost of ownership, operational uptime and future flexibility — not just construction budget." },
    { title: "Built to Last", desc: "We design buildings and systems that perform for 30+ years — through technology shifts, code changes and climate pressure." },
];

const aboutImages = [
    { url: "https://customer-assets.emergentagent.com/job_pes-modern/artifacts/hgxhqe96_IMG_0026.webp", label: "Research Lab Space" },
    { url: "https://images.unsplash.com/photo-1680992046626-418f7e910589?w=1200", label: "Data Center" },
    { url: "https://images.pexels.com/photos/18471441/pexels-photo-18471441.jpeg?w=1200", label: "Semiconductor Fab" },
    { url: "https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1200", label: "Medical Suite" },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <PageHeader
                eyebrow="About PES"
                title={<>Engineering as <span className="text-blue-400">craft</span>, not commodity.</>}
                description="We're a full-service MEP engineering design firm built around one conviction: that premium engineering shouldn't be a luxury. Since 2014, we've paired the top 1% of global talent with an always-on production model — delivering world-class MEP design, at pricing that wins, on timelines legacy firms can't meet."
            />

            {/* Image strip under header */}
            <section className="relative py-12 border-t border-white/5" data-testid="about-image-strip">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {aboutImages.map((img, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/5 group"
                            >
                                <img src={img.url} alt={img.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4">
                                    <div className="text-xs font-mono tracking-widest text-cyan-300">{img.label}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <CapabilityPillars />

            <section className="py-20 md:py-24 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <div className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.2em] uppercase text-cyan-400 mb-4">
                                <span className="w-8 h-px bg-cyan-400/60" /> Our Values
                            </div>
                            <h2 className="font-heading font-black text-white text-3xl md:text-4xl leading-tight tracking-tight">
                                What we believe about great engineering.
                            </h2>
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {values.map((v, i) => (
                                <motion.div
                                    key={v.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="p-6 rounded-2xl bg-slate-900/40 backdrop-blur-xl border border-white/5 hover:border-blue-500/30 transition-colors"
                                >
                                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mb-4" />
                                    <h3 className="font-heading font-bold text-white text-lg mb-2">{v.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <StatsSection />

            <ContactCTA compact />
        </div>
    );
}
